import { Button } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import MuiButton from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { SaveForceTruckArrivedForOrder } from "components/toolbars/SaveForceTruckArrivedForOrder";
import { da } from "date-fns/locale";
import React from "react";
import { Edit, SaveButton, Toolbar, useNotify, useTranslate } from "react-admin";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { checkCanCloneOrder, checkCustomerCanDeleteOrder } from "utils/checker/order";

import { useMutateForm } from "../../../api/useMutateForm";
import { orderWarningValidations } from "../../../components/_helpers/warningsConditions";
import { ActionsWithBackButton } from "../../../components/actionbars/ActionsWithBackButton";
import { DocumentsFieldBlock } from "../../../components/fields/DocumentsField";
import { SimpleFormAdv } from "../../../components/forms/SimpleFormAdv";
import { OSFCUserRoles, OrderStatus } from "../../../entities";
import { useGetRedirectToDeviationCreate } from "../../../utils/businessLogic/redirectToDeviationCreate";
import { getDiffValues } from "../../../utils/diff";
import { ArrivedPopupProvider } from "../forms/ArrivedPopupContextManager";
import { FormCustomer } from "../forms/FormCustomer";
import { FormCustomerTransporter } from "../forms/FormCustomerTransporter";
import { FormManager } from "../forms/FormManager";
import { FormTransporter } from "../forms/FormTransporter";
import { transformCustomFieldData } from "../forms/shared";
import { loadingDisable, unloadingDisable } from "../Show/Tabs";

export const EditFormWrapper = ({ children, ...rest }) => {
	const notify = useNotify();
	const redirectToDeviationCreate = useGetRedirectToDeviationCreate(rest.record);

	const [mutateForm, state] = useMutateForm(
		"update",
		rest.resource,
		rest.basePath,
		rest.record.id,
		{},
		redirectToDeviationCreate
	);
	const [mutatePartialForm, statePartial] = useMutateForm(
		"updatePartial",
		rest.resource,
		rest.basePath,
		rest.record.id,
		{},
		redirectToDeviationCreate
	);

	if (!React.isValidElement(children)) {
		return children;
	}
	const mutateFormAndEmit = (data, ...args) => {
		if (data["isUpdatePartial"]) {
			delete data["isUpdatePartial"];
			const transformed = getDiffValues(transformCustomFieldData(data), rest.record);
			if (!transformed) {
				notify("ra.action.update_warnings.no_changes", "warning");
				return;
			}
			transformed.id = rest.record.id;
			const result = mutatePartialForm(transformed, ...args);
			return result;
		} else {
			const transformed = transformCustomFieldData(data);
			const result = mutateForm(transformed, ...args);
			return result;
		}
	};

	return React.cloneElement(children, {
		...rest,
		save: mutateFormAndEmit,
	});
};

const getOrderWarning = (record) => {
	if (record?.deleted_at) {
		return "Order is deleted";
	}
};

const useStyles = makeStyles((theme) => ({
	// Global rules added so we can enable overflow to use sticky toolbar
	"@global": {
		".MuiCard-root:has(.overflow-enabled)": {
			overflow: "visible !important",
		},
	},
	stickyToolbar: {
		position: "sticky",
		bottom: 0,
		display: "flex",
		gap: "8px",
		backgroundColor: "#fff",
		zIndex: 1000,
		padding: "14px 16px",
		marginTop: "24px",
		marginLeft: "16px",
		marginRight: "-16px",
		boxShadow: "0 -2px 8px rgba(0,0,0,0.12)",
		borderTop: "1px solid #e0e0e0",
		minHeight: "48px !important",
	},
}));

// TODO - As OrderEditBase component not used we can move SaveWithTwoOptionsToolbar to EditTab or other component
export const SaveWithTwoOptionsToolbar = (props) => {
	const history = useHistory();
	const translate = useTranslate();
	const classes = useStyles();

	const SaveComp = props.saveComp ?? SaveButton;

	return (
		<Toolbar {...props} className={`${classes.stickyToolbar} overflow-enabled`}>
			<SaveComp
				{...props}
				label={translate("resources.orders.buttons.save")}
				redirect={false}
				submitOnEnter={false}
			/>
			<SaveComp
				{...props}
				label={translate("resources.orders.buttons.save-and-exit")}
				redirect="list"
				submitOnEnter={false}
			/>
			<Button
				variant="contained"
				color="primary"
				onClick={() => {
					history.goBack();
				}}
			>
				Cancel
			</Button>
		</Toolbar>
	);
};

export const OrderEditBase = (Form, name, actionsProps) => {
	const OrderEditComponent = (_props) => {
		const props = { ..._props };

		const isDraft = window.location.href.indexOf("draft-orders") !== -1;
		if (isDraft) {
			props.resource = "orders";
			props.title = "resources.draft-orders.name";
		}

		const toolbarCompProps = {
			warningValidations: orderWarningValidations,
			// TODO: It doen't work with custom code. Investigate why
			// saveButtonExProps: {
			// 	IsChangedRecordOnSavingCheckNeeded: true,
			// },
			...(props.permissions &&
				OSFCUserRoles.has(props.permissions.role) && { saveComp: SaveForceTruckArrivedForOrder }),
		};

		return (
			<ArrivedPopupProvider>
				<Edit
					{...props}
					undoable={false}
					actions={
						<ActionsWithBackButton
							{...actionsProps}
							cloneMutableValues={{
								reference: null,
								origin_id: props.id,
								status: OrderStatus.BOOKED,
							}}
						/>
					}
				>
					<EditFormWrapper>
						<SimpleFormAdv
							children={<Form />}
							toolbarComp={SaveWithTwoOptionsToolbar}
							toolbarCompProps={toolbarCompProps}
						/>
					</EditFormWrapper>
				</Edit>
				<Edit {...props} undoable={false} actions={null}>
					<DocumentsFieldBlock {...props} variant="elevation" showDownloadAll={true} />
				</Edit>
			</ArrivedPopupProvider>
		);
	};
	return OrderEditComponent;
};

export const OrderEditByManager = OrderEditBase(FormManager, "Manager", {
	// Info: don't need to have Delete button for manager as they can change to Delete status (except DRAFT)
	// hasDelete: true,
	hasClone: checkCanCloneOrder,
	hasChange: (record) => record?.status !== OrderStatus.DRAFT,
	hasExtraButtons: (record) => record?.status !== OrderStatus.DRAFT,
	hasDelete: (record) => record?.status === OrderStatus.DRAFT && !record?.deleted_at,
	hasWarning: getOrderWarning,
});
export const OrderEditByCustomer = OrderEditBase(FormCustomer, "Customer", {
	hasDelete: checkCustomerCanDeleteOrder,
	hasClone: checkCanCloneOrder,
	hasExtraButtons: (record) => record?.status !== OrderStatus.DRAFT,
	hasWarning: getOrderWarning,
});
export const OrderEditByCustomerTransporter = OrderEditBase(
	FormCustomerTransporter,
	"Customer and transporter",
	{
		ifCustomerTransporterOrderEdit: true,
		hasExtraButtons: (record) => record?.status !== OrderStatus.DRAFT,
		hasWarning: getOrderWarning,
	}
);
export const OrderEditByTransporter = OrderEditBase(FormTransporter, "Transporter", {
	hasExtraButtons: (record) => record?.status !== OrderStatus.DRAFT,
	hasWarning: getOrderWarning,
});
